import React, { useState, useEffect, useCallback } from 'react';
import { HiChevronLeft, HiChevronRight, HiExternalLink } from 'react-icons/hi';
import HeroSection from '../components/ui/HeroSection';
import ChalkboardBackground from '../components/ui/ChalkboardBackground';
import Button from '../components/ui/Button';
import siteContent from '../data/siteContent.json';
import homeBg from '../assets/images/bb-background.png';
import prepBg from '../assets/images/prep-banner.png';
import bridalHair from '../assets/images/prep-guides/bridal-hair.png';
import bridalMakeup from '../assets/images/prep-guides/bridal-makeup.png';
import bridalSuite from '../assets/images/prep-guides/bridal-suite.png';
import Seo from '../components/Seo';

const PreparationGuide = () => {
  const guides = [
    {
      image: bridalHair,
      title: 'Bridal Hair',
      category: 'Hair'
    },
    {
      image: bridalMakeup,
      title: 'Bridal Makeup',
      category: 'Makeup'
    },
    {
      image: bridalSuite,
      title: 'Bridal Suite',
      category: 'Day Of'
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? guides.length - 1 : prevIndex - 1
    );
  }, [guides.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) => 
      prevIndex === guides.length - 1 ? 0 : prevIndex + 1
    );
  }, [guides.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrevious]);

  return (
    <>
      <Seo
        title="Bridal Preparation Guide"
        description="How to prepare for your bridal hair and makeup with Blush & Bobby — skin prep, hair washing, timelines, and day-of tips for Kansas City brides."
        path="/preparation-guide"
      />
      {/* Hero */}
      <HeroSection title="Preparation Guide" backgroundImage={prepBg} overlayColor="bg-dusty-rose/25" />

      {/* Intro and Guide Images */}
      <ChalkboardBackground className="py-20">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-lg text-chalk/80 leading-relaxed mb-4">
                We have curated a perfect timeline and guide to help you prepare for whatever services you are getting. 
                Our comprehensive preparation guides ensure you're ready for your special day.
              </p>
            </div>

            {/* Navigation Tabs */}
            <div className="max-w-2xl mx-auto mb-12">
              <div className="flex flex-wrap justify-center gap-3">
                {guides.map((guide, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 border shadow-sm ${
                      index === currentIndex
                        ? 'bg-dusty-rose text-chalk border-dusty-rose scale-105'
                        : 'bg-charcoal-dark/40 text-dusty-rose/70 border-dusty-rose/20 hover:bg-dusty-rose/10 hover:text-dusty-rose'
                    }`}
                  >
                    {guide.title}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Image Carousel */}
            <div className="relative">
              {/* Main Image Container */}
              <div className="relative bg-charcoal-dark/40 rounded-2xl shadow-2xl overflow-hidden border border-dusty-rose/20">
                <div className="relative aspect-[3/4] bg-charcoal-dark/20">
                  <img 
                    src={guides[currentIndex].image} 
                    alt={guides[currentIndex].title}
                    className="w-full h-full object-contain"
                  />
                </div>

                {/* Navigation Arrows */}
                <button
                  onClick={goToPrevious}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-charcoal-dark/80 hover:bg-dusty-rose text-chalk p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-110 z-10 border border-dusty-rose/20"
                  aria-label="Previous guide"
                >
                  <HiChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={goToNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-charcoal-dark/80 hover:bg-dusty-rose text-chalk p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-110 z-10 border border-dusty-rose/20"
                  aria-label="Next guide"
                >
                  <HiChevronRight className="h-6 w-6" />
                </button>
              </div>

              {/* Navigation Controls */}
              <div className="mt-8">
                {/* Dot Indicators */}
                <div className="flex justify-center gap-2 mb-4">
                  {guides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`h-2.5 rounded-full transition-all duration-200 ${
                        index === currentIndex
                          ? 'bg-dusty-rose w-10 shadow-sm'
                          : 'bg-dusty-rose/15 w-2.5 hover:bg-dusty-rose/30'
                      }`}
                      aria-label={`Go to ${guides[index].title}`}
                    />
                  ))}
                </div>

                {/* Page Counter */}
                <div className="flex flex-col items-center gap-3">
                  <div className="text-sm text-dusty-rose/50 font-medium">
                    Guide {currentIndex + 1} of {guides.length}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ChalkboardBackground>

      {/* Final CTA */}
      <ChalkboardBackground 
        as="section" 
        backgroundImage={homeBg}
        backgroundOpacity={0.5}
        className="py-12 md:py-16 lg:py-20"
      >
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-headline font-semibold mb-6 text-chalk uppercase tracking-wider">
              Let's Create Your Dream Look
            </h2>
            <Button
              href={siteContent.social.linktree}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
              size="lg"
              className="!text-xs"
            >
              Check Availability
              <HiExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </ChalkboardBackground>
    </>
  );
};

export default PreparationGuide;
